import { Routes, Route, Navigate } from "react-router-dom";
import "./App.css";
import LoginPage from "./components/auth/LoginPage";
import SignUpPage from "./components/auth/SignUpPage";
import SetPasswordPage from "./components/auth/SetPasswordPage";
import HandoffPage from "./components/auth/HandoffPage";
import Dashboard from "./components/Dashboard";
import IntegrationDetail from "./components/IntegrationDetail";
import WorkflowsLayout from "./components/workflows/WorkflowsLayout";
import WorkflowsHome from "./components/workflows/WorkflowsHome";
import WorkflowDetail from "./components/workflows/WorkflowDetail";
import WorkflowStudio from "./components/workflows/WorkflowStudio";
import NotificationSettings from "./components/workflows/NotificationSettings";
import ProfileSettings from "./components/ProfileSettings";
import CompanySettings from "./components/CompanySettings";
import DeliveryAccess from "./components/DeliveryAccess";
import AskOrbit from "./components/AskOrbit";
import Sidebar from "./components/Sidebar";
import TopBar from "./components/TopBar";
import ProtectedRoute from "./routes/ProtectedRoute";
import AdminProtectedRoute from "./routes/AdminProtectedRoute";
import AdminPortal from "./components/admin/AdminPortal";
import InHouseBuilder from "./components/inHouseBuilder/InHouseBuilder";
import UserGuidePage from "./components/UserGuidePage";
import { isAuthenticated } from "./services/authService";

function App() {
  const userIsAuthenticated = isAuthenticated();

  return (
    <div className="App min-h-screen bg-gray-50">
      <Routes>
        {/* Public Routes */}
        <Route
          path="/"
          element={
            userIsAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignUpPage />} />
        <Route path="/set-password" element={<SetPasswordPage />} />
        {/* LexAI → Orbit SSO handoff; receives the id_token via postMessage */}
        <Route path="/handoff" element={<HandoffPage />} />

        {/* Protected Routes with Sidebar Layout */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <div className="flex h-screen overflow-hidden">
                <Sidebar />
                <div className="flex-1 flex flex-col overflow-hidden">
                  <TopBar />
                  <Dashboard />
                </div>
              </div>
            </ProtectedRoute>
          }
        />

        <Route
          path="/integration/:integrationId"
          element={
            <ProtectedRoute>
              <div className="flex h-screen overflow-hidden">
                <Sidebar />
                <IntegrationDetail />
              </div>
            </ProtectedRoute>
          }
        />

        {/* The in-house list lives on the Dashboard's In-House tab. */}
        <Route
          path="/integrations/in-house"
          element={<Navigate to="/dashboard?tab=in-house" replace />}
        />

        {/* In-house integration builder (org admins; the API enforces it too) */}
        {["/integrations/in-house/new", "/integrations/in-house/:integrationId"].map(
          (path) => (
            <Route
              key={path}
              path={path}
              element={
                <ProtectedRoute>
                  <div className="flex h-screen overflow-hidden">
                    <Sidebar />
                    <InHouseBuilder />
                  </div>
                </ProtectedRoute>
              }
            />
          )
        )}

        <Route
          path="/ask-orbit"
          element={
            <ProtectedRoute>
              <div className="flex h-screen overflow-hidden">
                <Sidebar />
                <div className="flex-1 flex flex-col overflow-hidden">
                  <TopBar />
                  <AskOrbit />
                </div>
              </div>
            </ProtectedRoute>
          }
        />

        <Route
          path="/workflows"
          element={
            <ProtectedRoute>
              <div className="flex h-screen overflow-hidden">
                <Sidebar />
                <div className="flex-1 flex flex-col overflow-hidden">
                  <TopBar />
                  <WorkflowsLayout />
                </div>
              </div>
            </ProtectedRoute>
          }
        >
          <Route index element={<WorkflowsHome />} />
          <Route path="notifications" element={<NotificationSettings />} />
        </Route>

        <Route
          path="/workflows/:id/edit"
          element={
            <ProtectedRoute>
              <div className="flex h-screen overflow-hidden">
                <Sidebar />
                <WorkflowStudio />
              </div>
            </ProtectedRoute>
          }
        />

        <Route
          path="/workflows/:id"
          element={
            <ProtectedRoute>
              <div className="flex h-screen overflow-hidden">
                <Sidebar />
                <WorkflowDetail />
              </div>
            </ProtectedRoute>
          }
        />

        <Route
          path="/profile-settings"
          element={
            <ProtectedRoute>
              <div className="flex h-screen overflow-hidden">
                <Sidebar />
                <div className="flex-1 flex flex-col overflow-hidden">
                  <TopBar />
                  <ProfileSettings />
                </div>
              </div>
            </ProtectedRoute>
          }
        />

        <Route
          path="/company-settings"
          element={
            <ProtectedRoute>
              <div className="flex h-screen overflow-hidden">
                <Sidebar />
                <div className="flex-1 flex flex-col overflow-hidden">
                  <TopBar />
                  <CompanySettings />
                </div>
              </div>
            </ProtectedRoute>
          }
        />

        <Route
          path="/delivery-access"
          element={
            <ProtectedRoute>
              <div className="flex h-screen overflow-hidden">
                <Sidebar />
                <div className="flex-1 flex flex-col overflow-hidden">
                  <TopBar />
                  <DeliveryAccess />
                </div>
              </div>
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <AdminProtectedRoute>
              <div className="flex h-screen overflow-hidden">
                <Sidebar />
                <div className="flex-1 flex flex-col overflow-hidden">
                  <TopBar />
                  <AdminPortal />
                </div>
              </div>
            </AdminProtectedRoute>
          }
        />

        {/* The Orbit User Guide fills its own tab ("User Guide" in the left menu opens it) */}
        <Route
          path="/user-guide"
          element={
            <ProtectedRoute>
              <UserGuidePage />
            </ProtectedRoute>
          }
        />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}

export default App;
